import React, { useEffect, useState } from 'react';
import { AlertTriangle, CalendarCheck, RefreshCw, UserCheck } from 'lucide-react';
import {
  apiMessage,
  createBooking,
  dismissBooking,
  getBookingLive,
  updateBooking,
} from '../services/serviceInboxApi';
import { AppointmentList } from './Appointments';
import { JsonButton } from './JsonModal';
import { matchedCustomer } from './ManualTask';

/**
 * Booking — the Mailbox's Appointment section: a ServiceTitan booking for the
 * email's matched customer, made and edited by hand.
 *
 * A booking lands in ServiceTitan's Bookings screen, where a CSR schedules it
 * into a job. Nothing books on its own (2026-09-28); this is the only way one
 * is made. Only for the matched customer, like a task: the one a person marked,
 * else the one the lookup settled on. One booking per email, and an email may
 * have a task as well.
 *
 * Once made, the booking is read live from ServiceTitan on open. Its start,
 * priority and summary can be changed, or it can be dismissed, until it is
 * converted to a job or dismissed. The time is entered and shown in this
 * browser's own time zone and sent as UTC.
 */

export const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];

// A stated window's usual start, to prefill the time. "any" states none.
const WINDOW_START = { morning: '08:00', afternoon: '12:00', evening: '16:00' };

const STATUS = {
  New: { label: 'New', className: 'bg-blue-50 text-blue-800 ring-blue-200' },
  Accepted: { label: 'Accepted', className: 'bg-indigo-50 text-indigo-800 ring-indigo-200' },
  Converted: { label: 'Converted to a job', className: 'bg-green-50 text-green-800 ring-green-200' },
  Dismissed: { label: 'Dismissed', className: 'bg-gray-100 text-gray-500 ring-gray-200' },
};

const PRIORITY_CLASS = {
  Urgent: 'bg-red-50 text-red-700 ring-red-200',
  High: 'bg-amber-50 text-amber-800 ring-amber-200',
};

const Pill = ({ className, children }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-px text-xs font-medium ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

const pad = (n) => String(n).padStart(2, '0');

// ServiceTitan writes seven fractional digits; trim to milliseconds to parse.
const parseSt = (iso) => (iso ? new Date(iso.replace(/(\.\d{3})\d+/, '$1')) : null);

const fmtWhen = (iso) => {
  const d = parseSt(iso);
  return d ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
};

// A date and time typed in this browser's zone, as an ISO time in UTC; null
// when no date was given.
export const startIso = (date, time) =>
  date ? new Date(`${date}T${time || '08:00'}`).toISOString() : null;

// The other way: a UTC time as the date and time inputs' values, local.
const localParts = (iso) => {
  const d = parseSt(iso);
  if (!d || Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const inputClass = 'mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm';

// Start, priority and summary: the fields both the new-booking and the edit
// form carry.
const Fields = ({ value, onChange }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="text-gray-500">Date</span>
          <input type="date" className={inputClass} value={value.date} onChange={set('date')} />
        </label>
        <label className="block text-sm">
          <span className="text-gray-500">Time</span>
          <input
            type="time"
            className={inputClass}
            value={value.time}
            onChange={set('time')}
            disabled={!value.date}
          />
        </label>
        <label className="col-span-2 block text-sm sm:col-span-1">
          <span className="text-gray-500">Priority</span>
          <select className={inputClass} value={value.priority} onChange={set('priority')}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-gray-500">Summary</span>
        <textarea
          className={inputClass}
          rows={3}
          maxLength={2000}
          value={value.summary}
          onChange={set('summary')}
        />
      </label>
    </div>
  );
};

// What the extraction asked for, to prefill a new booking.
export const bookingDefaults = (stored) => {
  const a = stored.appointment || {};
  return {
    date: a.preferred_date || '',
    time: a.preferred_date ? WINDOW_START[a.preferred_window] || '' : '',
    priority: a.urgent ? 'High' : 'Normal',
    summary: stored.summary || '',
  };
};

// `autoOpen`: start with the form open (the card's quick action), not the button.
const NewBooking = ({ stored, onBooked, autoOpen = false }) => {
  const picked = matchedCustomer(stored);
  const [open, setOpen] = useState(autoOpen);
  const [form, setForm] = useState(() => bookingDefaults(stored));
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!picked) {
    return (
      <div className="text-sm text-gray-500">Mark the customer as the match to create a booking.</div>
    );
  }
  if (picked.is_rnc) {
    return <div className="text-sm text-gray-500">RNC is out of scope: no booking.</div>;
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        <CalendarCheck className="mr-1.5 h-4 w-4" />
        Create booking…
      </button>
    );
  }

  const ready = form.summary.trim().length > 0;
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const booking = await createBooking({
        email_id: stored.email_id,
        tenant_id: picked.tenant_id,
        customer_id: picked.customer_id,
        summary: form.summary.trim(),
        start: startIso(form.date, form.time),
        priority: form.priority,
      });
      onBooked(booking);
      if (booking.status === 'created') setOpen(false);
      else setError(booking.reason || 'ServiceTitan did not return a booking.');
    } catch (err) {
      setError(apiMessage(err, 'Could not create the booking.'));
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  // Opened from the card's quick action (`autoOpen`), the panel around the
  // form carries the title and the ×, so the form draws neither.
  return (
    <div className={autoOpen ? 'space-y-3' : 'space-y-3 rounded-md border border-gray-200 bg-white p-3'}>
      {!autoOpen && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">New ServiceTitan booking</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      )}
      <div className="rounded border border-gray-200 px-3 py-2 text-sm">
        <div className="flex flex-wrap items-center gap-x-2">
          <UserCheck className="h-4 w-4 shrink-0 text-green-700" />
          <span className="font-semibold text-gray-900">{picked.name || '(no name)'}</span>
          <span className="tabular-nums text-gray-500">ST # {picked.customer_id}</span>
        </div>
        {picked.office && <div className="text-gray-600">{picked.office}</div>}
        {picked.address && <div className="text-gray-600">{picked.address}</div>}
      </div>
      <Fields value={form} onChange={setForm} />
      <div className="text-xs text-gray-500">
        {form.date
          ? 'The time is in your own time zone.'
          : 'No date: the CSR who schedules the booking picks the time.'}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      {confirming ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Create a booking for <strong>{picked.name}</strong> (ST # {picked.customer_id},{' '}
              {picked.office})? It goes to ServiceTitan&apos;s Bookings screen for a CSR to schedule.
            </span>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create booking'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!ready}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Review and create…
        </button>
      )}
    </div>
  );
};

// The booking as ServiceTitan holds it now, read on open and on Refresh.
const useLiveBooking = (tenantId, bookingId) => {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!tenantId || !bookingId) return undefined;
    let live = true;
    setState((s) => ({ ...s, error: null, loading: true }));
    getBookingLive(tenantId, bookingId)
      .then((data) => live && setState({ data, error: null, loading: false }))
      .catch(
        (err) =>
          live &&
          setState((s) => ({
            ...s,
            error: apiMessage(err, 'Could not read the booking from ServiceTitan.'),
            loading: false,
          }))
      );
    return () => {
      live = false;
    };
  }, [tenantId, bookingId, nonce]);
  return {
    ...state,
    set: (data) => setState({ data, error: null, loading: false }),
    reload: () => setNonce((n) => n + 1),
  };
};

const EditBooking = ({ booking, onSave, onCancel }) => {
  const [form, setForm] = useState(() => ({
    ...localParts(booking.start),
    priority: booking.priority || 'Normal',
    summary: booking.summary || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const before = {
    start: booking.start ? parseSt(booking.start).toISOString() : null,
    priority: booking.priority || 'Normal',
    summary: booking.summary || '',
  };
  const changes = {};
  const start = startIso(form.date, form.time);
  if (start && start !== before.start) changes.start = start;
  if (form.priority !== before.priority) changes.priority = form.priority;
  if (form.summary.trim() && form.summary.trim() !== before.summary.trim()) {
    changes.summary = form.summary.trim();
  }
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(changes);
    } catch (err) {
      setError(apiMessage(err, 'Could not change the booking.'));
      setSaving(false);
    }
  };
  return (
    <div className="mt-3 space-y-3 rounded-md border border-gray-200 bg-white p-3">
      <Fields value={form} onChange={setForm} />
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || Object.keys(changes).length === 0}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const DismissBooking = ({ onDismiss, onCancel }) => {
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dismiss = async () => {
    setSaving(true);
    setError(null);
    try {
      await onDismiss(memo.trim());
    } catch (err) {
      setError(apiMessage(err, 'Could not dismiss the booking.'));
      setSaving(false);
    }
  };
  return (
    <div className="mt-3 space-y-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Dismiss this booking? It leaves ServiceTitan&apos;s Bookings screen.</span>
      </div>
      <label className="block">
        <span className="text-amber-900">Reason (optional)</span>
        <input
          className={`${inputClass} bg-white`}
          maxLength={1000}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
      </label>
      {error && <div className="text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={dismiss}
          disabled={saving}
          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Dismissing…' : 'Dismiss booking'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <>
    <dt className="text-gray-500">{label}</dt>
    <dd className="break-words text-gray-900">{children}</dd>
  </>
);

const LiveBooking = ({ record, onInspect }) => {
  const live = useLiveBooking(record.tenant_id, record.booking_id);
  const [mode, setMode] = useState(null); // 'edit' | 'dismiss'
  const b = live.data?.booking;
  const status = b && (STATUS[b.status] || { label: b.status, className: 'bg-gray-100 text-gray-700 ring-gray-200' });
  const editable = b && (b.status === 'New' || b.status === 'Accepted');
  const done = (data) => {
    live.set(data);
    setMode(null);
  };
  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <CalendarCheck className="h-4 w-4 text-gray-500" />
          Booking # <span className="tabular-nums">{record.booking_id}</span>
        </span>
        {status && <Pill className={status.className}>{status.label}</Pill>}
        {b?.priority && (
          <Pill className={PRIORITY_CLASS[b.priority] || 'bg-white text-gray-600 ring-gray-200'}>
            {b.priority}
          </Pill>
        )}
        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={live.reload}
            disabled={live.loading}
            className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
            title="Read the booking from ServiceTitan again"
            aria-label="Refresh the booking"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${live.loading ? 'animate-spin' : ''}`} />
          </button>
          <JsonButton label="View the ServiceTitan booking" onClick={onInspect} />
        </span>
      </div>
      {!b && live.loading && <div className="text-sm text-gray-500">Reading the booking from ServiceTitan…</div>}
      {live.error && <div className="text-sm text-red-700">{live.error}</div>}
      {b && (
        <>
          <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5 text-sm">
            <Row label="Start">{b.start ? fmtWhen(b.start) : 'Not set'}</Row>
            {b.jobId != null && <Row label="Job">ST # {b.jobId}</Row>}
            {b.summary && <Row label="Summary">{b.summary}</Row>}
            <Row label="Created">{fmtWhen(b.createdOn)}</Row>
          </dl>
          {editable && !mode && (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setMode('edit')}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Edit…
              </button>
              <button
                type="button"
                onClick={() => setMode('dismiss')}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
              >
                Dismiss…
              </button>
            </div>
          )}
          {mode === 'edit' && (
            <EditBooking
              booking={b}
              onCancel={() => setMode(null)}
              onSave={async (changes) =>
                done(await updateBooking(record.tenant_id, record.booking_id, changes))
              }
            />
          )}
          {mode === 'dismiss' && (
            <DismissBooking
              onCancel={() => setMode(null)}
              onDismiss={async (memo) =>
                done(await dismissBooking(record.tenant_id, record.booking_id, memo))
              }
            />
          )}
        </>
      )}
      <div className="mt-3 border-t border-gray-200 pt-2 text-xs text-gray-500">
        Created by {record.created_by || 'someone'}
      </div>
    </>
  );
};

// What a `{ }` on a booking opens: the booking, read live again.
export const bookingView = (record) => ({
  title: `ServiceTitan booking # ${record.booking_id}`,
  subtitle: 'Live from ServiceTitan',
  load: () => getBookingLive(record.tenant_id, record.booking_id),
  sections: [{ label: 'Booking', pick: (d) => d.booking }],
});

// Shown only when the automatic booking rules would book this email. They are
// switched off; this lets them be checked against real mail first. An email
// they would not book says nothing here (the reason is in the AI record).
export const WouldBook = ({ preview }) =>
  preview?.eligible ? (
    <div className="mb-2 rounded border border-dashed border-violet-300 bg-violet-50 px-2 py-1 text-xs text-violet-800">
      <span className="font-medium">Automatic booking (off):</span> would book this email.
    </div>
  ) : null;

// `appointments`: what the customer already has open in ServiceTitan (the
// card's `useAppointments`), listed under the booking and its Create button.
const BookingSection = ({ stored, onBooked, onInspect, appointments = null, autoOpen = false }) => (
  <>
    <WouldBook preview={stored.would_book} />
    <BookingBody stored={stored} onBooked={onBooked} onInspect={onInspect} autoOpen={autoOpen} />
    <AppointmentList appointments={appointments} />
  </>
);

const BookingBody = ({ stored, onBooked, onInspect, autoOpen = false }) => {
  const record = stored.booking;
  if (record?.status === 'created' && record.booking_id && record.tenant_id) {
    return <LiveBooking record={record} onInspect={() => onInspect(bookingView(record))} />;
  }
  if (record?.status === 'pending') {
    return (
      <div className="text-sm text-gray-500">
        Being created by {record.created_by || 'someone'}. If this stays, check ServiceTitan before
        trying again.
      </div>
    );
  }
  return (
    <>
      {record?.status === 'error' && (
        <div className="mb-2 text-sm text-red-700 break-words">
          The last attempt failed: {record.reason || 'no reason given'}
        </div>
      )}
      <NewBooking stored={stored} onBooked={onBooked} autoOpen={autoOpen} />
    </>
  );
};

export default BookingSection;
